// Configuración de transferencias (EN TRÁNSITO): pantalla propia (permisable por
// perfil), antes era un modal dentro de la pestaña "Tránsito y diferencias" de
// Transferencias. Controla el flujo envío → tránsito → recepción entre bodegas.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Switch, fieldCls, invRpc } from './shared';
import type { TransfConfig } from './Transito';

export function TransferenciasConfig() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<TransfConfig>({
    queryKey: ['inv-transf-config'], queryFn: () => invRpc<TransfConfig>('inv_transf_config_get', {}),
  });
  const [f, setF] = useState<TransfConfig | null>(null);
  const cfg = f ?? data ?? null;

  const guardar = useMutation({
    mutationFn: () => invRpc('inv_transf_config_set', { _data: cfg }),
    onSuccess: () => { toast.success('Configuración guardada'); void qc.invalidateQueries({ queryKey: ['inv-transf-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold"><Settings2 className="w-5 h-5" /> Configuración de transferencias</h1>
        <p className="text-sm text-muted-foreground">
          Controla el flujo EN TRÁNSITO de las transferencias entre bodegas (envío → recepción).
        </p>
      </div>

      {isLoading || !cfg ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Flujo de tránsito</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm">
            <Switch checked={cfg.requiere_recepcion} onChange={(v) => setF({ ...cfg, requiere_recepcion: v })}
              label="Requiere recepción en destino (flujo EN TRÁNSITO)" />
            <p className="-mt-2 text-xs text-muted-foreground">Apagado: la transferencia entra al destino al confirmar (instantáneo, como siempre). Encendido: queda EN TRÁNSITO hasta que el destino la reciba contándola.</p>
            <Switch checked={cfg.recepcion_ciega} onChange={(v) => setF({ ...cfg, recepcion_ciega: v })}
              label="Recepción a ciegas (no muestra cantidades al contar)" />
            <Switch checked={cfg.emite_guia} onChange={(v) => setF({ ...cfg, emite_guia: v })}
              label="Emitir guía de remisión SRI (obligatoria en Ecuador entre locales)" />
            <p className="-mt-2 text-xs text-muted-foreground">Con esto activo, cada transferencia enviada ofrece emitir su guía de remisión electrónica (código 06). Configura el código de establecimiento SRI de cada bodega en Inventario → Bodegas.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Campo label="Lead por defecto (días)">
                <input type="number" min={0} className={fieldCls} value={cfg.lead_default_dias}
                  onChange={(e) => setF({ ...cfg, lead_default_dias: Number(e.target.value) || 0 })} />
              </Campo>
              <Campo label="Alerta de atraso (+días sobre ETA)">
                <input type="number" min={0} className={fieldCls} value={cfg.atraso_alerta_dias}
                  onChange={(e) => setF({ ...cfg, atraso_alerta_dias: Number(e.target.value) || 0 })} />
              </Campo>
              <Campo label="Margen caducidad (días)">
                <input type="number" min={0} className={fieldCls} value={cfg.margen_caducidad_dias}
                  onChange={(e) => setF({ ...cfg, margen_caducidad_dias: Number(e.target.value) || 0 })} />
              </Campo>
            </div>
            <p className="text-xs text-muted-foreground">No se envían lotes que caducan antes del ETA + margen (guardarraíl farmacia).</p>
            <Campo label="Bloquear la caja del local si tiene transferencias sin recibir hace más de (días)">
              <input type="number" min={1} className={`${fieldCls} sm:w-48`} placeholder="Sin bloqueo"
                value={cfg.bloqueo_caja_dias ?? ''}
                onChange={(e) => setF({ ...cfg, bloqueo_caja_dias: e.target.value === '' ? null : Math.max(1, Number(e.target.value) || 1) })} />
            </Campo>
            <p className="-mt-2 text-xs text-muted-foreground">Vacío: sin bloqueo. Con un número, el turno de caja no abre en una bodega que tenga transferencias enviadas hace más de esos días sin recibir: el local recibe lo que le mandaron antes de vender. Los faltantes que nadie resuelve los vigila la auditoría continua.</p>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
